import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import { adminApi } from '../adminApi';
import { useAdminAuth } from '../adminAuth';

export function Admin2FAVerify() {
  const navigate = useNavigate();
  const { refresh } = useAdminAuth();
  const [code, setCode] = useState('');
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await adminApi('/v1/auth/admin/2fa/verify', { method: 'POST', body: { code, remember } });
      await refresh();
      navigate('/admin', { replace: true });
    } catch (err) {
      const e = err as { status?: number; body?: { error?: string; message?: string } };
      if (e.status === 401 && e.body?.error === 'totp_replay') {
        setError('This code has already been used. Wait for the next one.');
      } else if (e.status === 401) {
        setError('Invalid code.');
      } else if (e.body?.message) {
        setError(e.body.message);
      } else {
        setError('Verification failed.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="admin-auth-shell">
      <div className="admin-auth-card">
        <h1>Two-factor authentication</h1>
        <p className="admin-auth-sub">Enter the 6-digit code from your authenticator app.</p>
        <form onSubmit={onSubmit}>
          {error !== null ? <div className="admin-auth-banner error">{error}</div> : null}
          <label htmlFor="admin-totp-code">6-digit code</label>
          <input
            id="admin-totp-code"
            type="text"
            inputMode="numeric"
            pattern="\d{6}"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            required
            autoFocus
          />
          <div className="admin-checkbox-field">
            <input
              id="admin-remember-device"
              type="checkbox"
              className="admin-checkbox-input"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              disabled={submitting}
              aria-describedby="admin-remember-device-help"
            />
            <label htmlFor="admin-remember-device" className="admin-checkbox-label">
              Remember this device for 30 days
            </label>
          </div>
          <p id="admin-remember-device-help" className="admin-checkbox-help">
            Skips the code on this browser for 30 days. Use only on devices you trust — not shared
            or public computers.
          </p>
          <button type="submit" className="admin-primary-button" disabled={submitting}>
            {submitting ? 'Verifying…' : 'Sign in'}
          </button>
        </form>
      </div>
    </main>
  );
}
